import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { PageLoad } from '../components/ui';

// ── Icon helper ───────────────────────────────────────────────────────────────
function Ico({ d, size = 16, stroke = 1.75, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={style}>
      {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
  );
}

function fmt(n) { return Number(n).toLocaleString(); }

// ── Mini completion ring ──────────────────────────────────────────────────────
function MiniRing({ value, color = '#F5A623', size = 52, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, value));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: 'none' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EEF1F5" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${v * c} ${c}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function RPHero({ range, setRange }) {
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  return (
    <div className="rp-hero">
      <div className="rp-hero-decor" aria-hidden="true">
        <svg viewBox="0 0 800 260" preserveAspectRatio="xMidYMid slice" width="100%" height="100%">
          <defs>
            <pattern id="rpgrid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M40 0H0V40" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="800" height="260" fill="url(#rpgrid)" />
          <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1.2">
            <path d="M-20 180 Q 100 150 220 165 T 460 148 T 700 135 T 920 150" />
            <path d="M-20 210 Q 100 190 220 196 T 460 180 T 700 170 T 920 182" />
          </g>
          <g fill="none" stroke="rgba(245,166,35,0.08)" strokeWidth="1">
            <circle cx="720" cy="60" r="60" />
            <circle cx="720" cy="60" r="110" />
          </g>
        </svg>
      </div>
      <div className="rp-hero-inner">
        <div className="rp-hero-top">
          <div>
            <div className="rp-hero-crumb">
              <span>Workspace</span>
              <span className="rp-crumb-sep">/</span>
              <span>Reports</span>
            </div>
            <h1 className="rp-hero-title">Reports <span style={{ color: '#F5A623' }}>Studio</span></h1>
            <p className="rp-hero-lede">
              Practicum completion analytics across programs, coordinators and compliance.
              <span className="rp-asof">As of {today}</span>
            </p>
          </div>
          <div className="rp-hero-actions">
            <div className="rp-range">
              {['All time', '90d', '30d', '7d'].map(r => (
                <button key={r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>
              ))}
            </div>
            <button className="rp-btn-ghost">
              <Ico d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" size={14} />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── KPI Band ──────────────────────────────────────────────────────────────────
function RPKpiBand({ summary }) {
  const { total, completed, inProgress, outstanding, completionRate, avgDays, totalHosts } = summary;
  const cards = [
    {
      eyebrow: 'Completion Rate',
      value: `${completionRate}%`,
      label: `${fmt(completed)} of ${fmt(total)} students complete`,
      color: '#F5A623',
      fill: completionRate / 100,
      ring: true,
    },
    {
      eyebrow: 'Ready for Practicum',
      value: fmt(inProgress),
      label: 'Students with docs complete',
      color: '#2A6FDB',
      fill: total > 0 ? inProgress / total : 0,
    },
    {
      eyebrow: 'Pending Documents',
      value: fmt(outstanding),
      label: 'Students in pre-req phase',
      color: '#E5484D',
      fill: total > 0 ? outstanding / total : 0,
    },
    {
      eyebrow: 'Active Hosts',
      value: fmt(totalHosts),
      label: 'Host partners in network',
      color: '#1F8A5B',
      fill: 1,
    },
  ];

  return (
    <div className="rp-kpi-band">
      {cards.map((k, i) => (
        <div key={i} className="rp-kpi-card">
          <div className="rp-kpi-top">
            <span className="rp-kpi-eyebrow" style={{ color: k.color }}>{k.eyebrow}</span>
          </div>
          {k.ring ? (
            <div className="rp-kpi-with-ring">
              <MiniRing value={k.fill} color={k.color} />
              <div className="rp-kpi-num">{k.value}</div>
            </div>
          ) : (
            <div className="rp-kpi-num">{k.value}</div>
          )}
          <div className="rp-kpi-label">{k.label}</div>
          <div className="rp-kpi-bar">
            <div className="rp-kpi-bar-fill"
              style={{ width: `${Math.max(2, Math.round(k.fill * 100))}%`, background: k.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Programs card ─────────────────────────────────────────────────────────────
const PROG_COLORS = ['#F5A623', '#2A6FDB', '#1F8A5B', '#7A5AE0', '#E5484D'];

function RPProgramsCard({ programs }) {
  const sorted = [...programs].sort((a, b) => b.total - a.total);
  return (
    <div className="rp-card">
      <div className="rp-card-head">
        <div>
          <div className="rp-eyebrow">Performance</div>
          <h3 className="rp-card-title">Students by Program</h3>
        </div>
        <span className="rp-card-meta">{programs.length} programs</span>
      </div>
      <div className="rp-programs">
        {sorted.map((p, i) => {
          const color = PROG_COLORS[i % PROG_COLORS.length];
          const compPct  = p.total > 0 ? (p.completed  / p.total) * 100 : 0;
          const readyPct = p.total > 0 ? (p.inProgress / p.total) * 100 : 0;
          const outPct   = p.total > 0 ? (p.outstanding / p.total) * 100 : 0;
          return (
            <div key={p.code} className="rp-program-row">
              <div className="rp-program-side">
                <span className="rp-program-code" style={{ background: color + '1A', color }}>{p.code}</span>
                <div>
                  <div className="rp-program-name">{p.name}</div>
                  <div className="rp-program-meta">{fmt(p.total)} students</div>
                </div>
              </div>
              <div className="rp-program-bar">
                <div className="rp-program-stack">
                  {p.completed  > 0 && <span style={{ width: `${compPct}%`,  background: '#1F8A5B' }} />}
                  {p.inProgress > 0 && <span style={{ width: `${readyPct}%`, background: '#2A6FDB' }} />}
                  {p.outstanding > 0 && <span style={{ width: `${outPct}%`,  background: '#E4E8EE' }} />}
                </div>
              </div>
              <div className="rp-program-num">
                <span className="rp-program-pct" style={{ color }}>{p.rate}%</span>
                <span className="rp-program-frac">{p.completed}/{p.total}</span>
              </div>
            </div>
          );
        })}
        {programs.length === 0 && <div className="rp-empty">No program data available</div>}
      </div>

      <div className="rp-prog-legend">
        {[
          { color: '#1F8A5B', label: 'Complete' },
          { color: '#2A6FDB', label: 'Ready' },
          { color: '#E4E8EE', label: 'Pre-Req', textColor: '#8C9DAE' },
        ].map(l => (
          <span key={l.label} className="rp-prog-legend-item">
            <i style={{ background: l.color }} />
            <span style={{ color: l.textColor }}>{l.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Coordinator leaderboard ───────────────────────────────────────────────────
const AV_COLORS = [
  'linear-gradient(135deg,#FBC559,#C97A0E)',
  'linear-gradient(135deg,#B7C5D2,#7A8FA0)',
  'linear-gradient(135deg,#E0C9A0,#A07840)',
];

function RPCoordinatorsCard({ pcs }) {
  return (
    <div className="rp-card">
      <div className="rp-card-head">
        <div>
          <div className="rp-eyebrow">Leaderboard</div>
          <h3 className="rp-card-title">By Coordinator</h3>
        </div>
        {pcs.length > 0 && <span className="rp-card-meta">{pcs.length} coordinators</span>}
      </div>
      {pcs.length === 0 ? (
        <div className="rp-empty">No coordinator assignments yet</div>
      ) : (
        <div className="rp-coords">
          {pcs.map((pc, i) => (
            <div key={pc.name} className={`rp-coord-row${i === 0 ? ' rp-coord-top' : ''}`}>
              <span className="rp-coord-rank">{i === 0 ? '🏆' : `#${i + 1}`}</span>
              <div className="rp-coord-avatar"
                style={{
                  background: AV_COLORS[Math.min(i, 2)] || '#EEF1F5',
                  color: i < 3 ? '#3D2710' : '#324A5F',
                }}>
                {pc.initials}
              </div>
              <div className="rp-coord-info">
                <div className="rp-coord-name">{pc.name}</div>
                <div className="rp-coord-role">{fmt(pc.total)} students · {pc.completed} complete</div>
              </div>
              <div className="rp-coord-bar">
                <div className="rp-coord-bar-fill"
                  style={{ width: `${pc.rate}%`, background: '#F5A623' }} />
              </div>
              <div className="rp-coord-pct">{pc.rate}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Document bottleneck heatmap ───────────────────────────────────────────────
const DOC_TYPES = [
  { key: 'missingFirstAid',     label: 'First Aid' },
  { key: 'missingPic',          label: 'PIC' },
  { key: 'missingImmunization', label: 'Immunization' },
  { key: 'missingResume',       label: 'Résumé' },
];

function heatStyle(missing, total) {
  if (!total || missing === 0) return { bg: '#F1F8F4', color: '#1F8A5B' };
  const pct = missing / total;
  if (pct < 0.25) return { bg: '#FBF1DD', color: '#B06A00' };
  if (pct < 0.50) return { bg: '#F8DEB9', color: '#8B4A00' };
  if (pct < 0.75) return { bg: '#F2A877', color: '#6B2900' };
  return { bg: '#E5484D', color: 'white' };
}

function RPBottleneckCard({ bottlenecks }) {
  return (
    <div className="rp-card">
      <div className="rp-card-head">
        <div>
          <div className="rp-eyebrow">Compliance</div>
          <h3 className="rp-card-title">Document Bottlenecks</h3>
        </div>
        <div className="rp-heatmap-legend">
          <span>Low</span>
          <div className="rp-heat-scale" />
          <span>High</span>
        </div>
      </div>
      {bottlenecks.length === 0 ? (
        <div className="rp-empty">No pre-req students with missing documents</div>
      ) : (
        <div className="overflow-x-scroll">
        <div className="rp-heatmap" style={{ gridTemplateColumns: `1fr repeat(${DOC_TYPES.length}, minmax(80px, 1fr))`, minWidth: 480 }}>
          <div />
          {DOC_TYPES.map(d => (
            <div key={d.key} className="rp-heatmap-col-label">{d.label}</div>
          ))}
          {bottlenecks.map(b => (
            <React.Fragment key={b.code}>
              <div className="rp-heatmap-row-label">
                <span className="rp-program-code" style={{ background: '#EEF1F5', color: '#324A5F', fontSize: 10 }}>
                  {b.code}
                </span>
                <span>{b.name}</span>
              </div>
              {DOC_TYPES.map(d => {
                const missing = b[d.key] || 0;
                const { bg, color } = heatStyle(missing, b.total);
                return (
                  <div key={d.key} className="rp-heatmap-cell" style={{ background: bg, color }}>
                    <strong>{missing}</strong>
                    <small>{b.total > 0 ? Math.round((missing / b.total) * 100) : 0}%</small>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}

// ── At-risk students ──────────────────────────────────────────────────────────
function RPAtRiskCard({ atRisk }) {
  return (
    <div className="rp-card">
      <div className="rp-card-head">
        <div>
          <div className="rp-eyebrow">Attention Needed</div>
          <h3 className="rp-card-title">Pre-Req Students</h3>
        </div>
        {atRisk.length > 0 && <span className="rp-card-meta">{atRisk.length} shown</span>}
      </div>
      {atRisk.length === 0 ? (
        <div className="rp-empty">No students in pre-req phase</div>
      ) : (
        <div className="rp-atrisk">
          {atRisk.map(s => (
            <div key={s.id} className="rp-atrisk-row">
              <div className="rp-atrisk-avatar">{s.avatar}</div>
              <div className="rp-atrisk-info">
                <div className="rp-atrisk-name">{s.name}</div>
                <div className="rp-atrisk-meta">
                  <span>{s.program}</span>
                  <span>·</span>
                  <span>{s.daysWaiting}d in pre-req</span>
                </div>
              </div>
              <div className="rp-atrisk-missing">
                {s.missing.map(m => <span key={m} className="rp-atrisk-chip">{m}</span>)}
              </div>
              <div className="rp-atrisk-days">
                <strong>{s.daysWaiting}</strong>
                <span>days</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Activity feed ─────────────────────────────────────────────────────────────
const TRIGGER_DOT = {
  agreement_sent:     '#F5A623',
  documents_required: '#2A6FDB',
  doc_reminder:       '#E5484D',
  goal_reached:       '#1F8A5B',
  completion_congrats:'#1F8A5B',
  host_nurture:       '#7A5AE0',
  weekly_pulse:       '#2A6FDB',
};

function triggerLabel(t) {
  return (t || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function timeAgo(dateStr) {
  const d = new Date(dateStr);
  const mins = Math.round((Date.now() - d) / 60000);
  if (mins < 60)  return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)   return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

function RPActivityCard({ activity }) {
  return (
    <div className="rp-card">
      <div className="rp-card-head">
        <div>
          <div className="rp-eyebrow">Timeline</div>
          <h3 className="rp-card-title">Recent Activity</h3>
        </div>
      </div>
      {activity.length === 0 ? (
        <div className="rp-empty">No recent email activity</div>
      ) : (
        <ul className="rp-activity">
          {activity.map((a, i) => (
            <li key={i} className="rp-act-row">
              <span className="rp-act-dot"
                style={{ background: TRIGGER_DOT[a.triggerType] || '#8C9DAE' }} />
              <div>
                <div className="rp-act-text">
                  <strong>{a.studentName || a.hostName || 'System'}</strong>
                  {' — '}{triggerLabel(a.triggerType)}
                </div>
                <div className="rp-act-time">{timeAgo(a.sentAt)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Status legend pill ────────────────────────────────────────────────────────
function StatusLegend() {
  return (
    <div className="rp-status-legend">
      {[
        { color: '#1F8A5B', label: 'Complete' },
        { color: '#2A6FDB', label: 'Ready' },
        { color: '#E5484D', label: 'Pre-Req' },
      ].map(l => (
        <span key={l.label} className="rp-status-legend-item">
          <i style={{ background: l.color }} />
          {l.label}
        </span>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange]   = useState('All time');
  const [error, setError]   = useState(null);

  useEffect(() => {
    api.get('/reports/studio')
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoad />;
  if (error || !data) {
    return (
      <div className="rp-page">
        <div className="rp-content">
          <div className="rp-empty" style={{ paddingTop: 80 }}>
            Failed to load report data{error ? `: ${error}` : ''}
          </div>
        </div>
      </div>
    );
  }

  const { summary, byProgram, byPC, atRisk, activity, bottlenecks } = data;

  return (
    <div className="rp-page">
      <RPHero range={range} setRange={setRange} />

      <div className="rp-content">
        <div className="rp-content-inner">

          <RPKpiBand summary={summary} />

          <div className="rp-row rp-two-eq">
            <RPProgramsCard programs={byProgram} />
            <RPCoordinatorsCard pcs={byPC} />
          </div>

          <div className="rp-row">
            <RPBottleneckCard bottlenecks={bottlenecks} />
          </div>

          <div className="rp-row rp-two-wide">
            <RPAtRiskCard atRisk={atRisk} />
            <RPActivityCard activity={activity} />
          </div>

        </div>
      </div>
    </div>
  );
}
